<template lang="pug">
  #email-modal.modal-card(style='width: auto')
    header.modal-card-head.is-flex-direction-column.no-border.pt-5
      WtTitle.mb-0.has-text-centered Edit Organization
    section.modal-card-body
      BField.mt-2(
        label="Name"
        label-for="name"
        :type="{ 'is-danger': nameRequiredMessage }"
        :message="nameRequiredMessage || ''"
      )
        BInput.mt-3.mr-5.is-flex-grow-1(
          v-model="name"
          placeholder="Organization Name"
          size="is-medium"
          @input="changesMade = true"
        )
      BField.mt-6(
        label="Status"
        label-for="active"
      )
        WtRadioQuestionInput
          template(v-slot:radioOne)
            BRadio(
              v-model="active"
              :native-value="1"
              @input="changesMade = true"
            ) Active
          template(v-slot:radioTwo)
            BRadio(
              v-model="active"
              :native-value="0"
              @input="changesMade = true"
            ) Inactive
      BField.mt-6(label="Edit Organization Admin")
        .columns.is-multiline.is-variable
          .column.max-width-100
            EntityAvatar.ml-4(
              :image="adminAvatar" 
              :size-class="isMobile ? 'is-64x64' : 'is-96x96'" 
              is-rounded
            )
            WtRouterLink.ml-4(
              v-if="adminUsername"
              to="ProfilePage"
              :params="{ username: adminUsername }"
              :text="'@'+adminUsername"
            )
      BField
        .columns.is-multiline.is-variable
          .column.max-width-100.mt-5
            .search-bar
              BAutocomplete(
                rounded
                icon="magnify"
                :data="autocompleteOptions"
                placeholder="Search by username"
                @select="onSelectAdmin"
                @typing="onSearch"
              )
                template(slot-scope="props")
                  | {{ props.option.username }}
          .column.max-width-100.mt-5
            .drop-down
              BSelect(
                placeholder="Choose Admin"
                v-model="adminId"
                @input="changesMade = true"
              )
                option(
                  v-for="(admin, index) in editedOrganization.members"
                  :key="index"
                  :value="admin.id"
                )
                  | {{ admin.username }}
      BField.mt-6(:label="editedOrganization.logo ? 'Edit your logo' : 'Upload your logo'")
        WtTile(child :col="5" child-class="no-padding")
          WtUploadImage(
            entity="organization"
            :currentImage.sync="editedOrganization.logo"
            @updateImage="newLogo = $event"
          )
    footer.modal-card-foot.no-border.pb-5
      .is-flex.is-flex-grow-1.is-justify-content-flex-end
        WtButton.button(@click.native="updateThisOrganization" :disabled="disableSave") Confirm
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EntityAvatar from "@/components/WtEntityAvatar/WtEntityAvatar";
import WtButton from "@@/WtButton/WtButton";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import WtRadioQuestionInput from "@@/WtRadioQuestionInput/WtRadioQuestionInput";
import WtRouterLink from "@@/WtRouterLink/WtRouterLink";
import WtUploadImage from "@@/WtUploadImage/WtUploadImage";

export default {
  name: "EditOrganizationModal",

  components: {
    EntityAvatar,
    WtTile,
    WtButton,
    WtTitle,
    WtRadioQuestionInput,
    WtRouterLink,
    WtUploadImage
  },

  props: {
    organizationId: {
      type: Number,
      required: true
    },

    organizationAdmin: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      changesMade: false,
      nameRequiredMessage: "",
      autocompleteOptions: [],
      adminUsername: "",
      adminAvatar: ""
    };
  },

  computed: {
    ...mapGetters("organizations", ["editedOrganization"]),
    ...mapGetters(["isMobile"]),

    disableSave() {
      if (!this.changesMade) {
        return true;
      }
      let disableSave = false;
      let requiredKeys = ["nameRequiredMessage"];
      requiredKeys.forEach(requiredKey => {
        if (this[requiredKey] !== "") {
          disableSave = true;
        }
      });
      return disableSave;
    },

    name: {
      set(name) {
        if (!name) {
          this.nameRequiredMessage = "The name field is required.";
        } else if (this.nameRequiredMessage) {
          this.nameRequiredMessage = "";
        }
        this.updateEditedOrganization({
          key: "name",
          value: name
        });
      },
      get() {
        return this.editedOrganization.name;
      }
    },

    active: {
      set(active) {
        this.updateEditedOrganization({
          key: "active",
          value: active
        });
      },
      get() {
        return this.editedOrganization.active;
      }
    },

    adminId: {
      set(adminId) {
        this.updateEditedOrganization({
          key: "adminId",
          value: adminId
        });
      },
      get() {
        return this.editedOrganization.adminId;
      }
    },

    newLogo: {
      set(newLogo) {
        if (newLogo) {
          this.changesMade = true;
        }
        this.updateEditedOrganization({
          key: "newLogo",
          value: newLogo
        });
      },
      get() {
        return this.editedOrganization.newLogo;
      }
    }
  },

  watch: {
    adminId(newAdminId) {
      const selectedAdmin = this.editedOrganization.members.find(
        member => member.id === newAdminId
      );

      if (selectedAdmin) {
        this.adminUsername = selectedAdmin.username;
        this.adminAvatar = selectedAdmin.avatar;
      }
    }
  },

  mounted() {
    if (this.organizationAdmin) {
      this.adminUsername = this.organizationAdmin.username;
      this.adminAvatar = this.organizationAdmin.avatar;
    }
  },

  methods: {
    ...mapActions("organizations", [
      "updateEditedOrganization",
      "updateOrganization"
    ]),
    ...mapActions("members", ["fetchMember"]),
    ...mapActions(["snackBar"]),

    closeEditModal() {
      this.$emit("editModalOpen", false);
    },

    async updateThisOrganization() {
      const formData = new FormData();
      formData.set("organizationId", this.organizationId);
      formData.set("name", this.name);
      formData.set("active", this.active);
      if (this.adminId) {
        formData.set("adminId", this.adminId);
      }
      if (this.newLogo) {
        formData.set("image", this.newLogo);
      }
      try {
        await this.updateOrganization({
          formData,
          organizationId: this.organizationId
        });
        this.snackBar({
          duration: 5000,
          type: "is-success",
          message: `Organization successfully updated!`
        });
        this.fetchMember({
          memberId: this.$auth.user().id,
          setMember: true
        });
        this.closeEditModal();
      } catch (err) {
        if (err.status !== 500) {
          this.snackBar({
            type: "is-danger",
            message: err.data.error.message
          });
        } else {
          this.snackBar({
            type: "is-danger",
            message: `There was an error. Please contact wellness@be-engaged.co`
          });
        }
        this.processing = false;
      }
    },

    onSearch(query) {
      const filteredMembers = this.editedOrganization.members.filter(member => {
        return member.username.toLowerCase().includes(query.toLowerCase());
      });
      this.autocompleteOptions = filteredMembers;
    },

    onSelectAdmin(option) {
      if (option.id !== this.adminId) {
        this.changesMade = true;
      }
      this.adminId = option.id;
      this.adminUsername = option.username;
      this.adminAvatar = option.avatar;
    }
  }
};
</script>

<style lang="sass" scoped>
.no-border
  border: none
.search-bar
  max-width: 90%
.drop-down
  max-width: 100%
</style>
